import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { Badge } from "@/components/badge";
import type { IExamConfig } from "@/apis/exam_configs/getExamConfigList";

interface ExamConfigListTableProps {
  examConfigs: IExamConfig[];
  isLoading: boolean;
  error: Error | null;
}

const ExamConfigListTable: React.FC<ExamConfigListTableProps> = ({
  examConfigs,
  isLoading,
  error,
}) => {
  const navigate = useNavigate();

  // Navigate to exam config detail page
  const handleViewExamConfig = (configId: string) => {
    navigate(`/exam_configs/${configId}`);
  };
  if (isLoading) {
    return (
      <div className="text-center py-8">Loading exam configurations...</div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        Error occurred: {error.toString()}
      </div>
    );
  }

  if (examConfigs.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        No exam configurations found.
      </div>
    );
  }

  const getTotalDuration = (subjects: IExamConfig["subjects"]) => {
    return subjects.reduce(
      (total, subject) => total + subject.duration_default,
      0
    );
  };

  const getTotalQuestions = (subjects: IExamConfig["subjects"]) => {
    return subjects.reduce(
      (total, subject) => total + subject.question_count_default,
      0
    );
  };

  const getCalculatorSubjects = (subjects: IExamConfig["subjects"]) => {
    return subjects.filter((subject) => subject.calculator_allowed).length;
  };

  return (
    <Table className="mt-4">
      <TableHead>
        <TableRow>
          <TableHeader>Configuration ID</TableHeader>
          <TableHeader>Subjects</TableHeader>
          <TableHeader>Total Duration (min)</TableHeader>
          <TableHeader>Total Questions</TableHeader>
          <TableHeader>Calculator Allowed</TableHeader>
          <TableHeader>Created</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {examConfigs.map((config) => (
          <TableRow
            key={config.id}
            className="cursor-pointer hover:bg-gray-50"
            onClick={() => handleViewExamConfig(config.id)}
          >
            <TableCell>
              <div className="font-medium">{config.id}</div>
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {config.subjects.map((subject, index) => (
                  <Badge key={index} color="blue">
                    {subject.subject} ({subject.duration_default}min)
                  </Badge>
                ))}
              </div>
            </TableCell>
            <TableCell className="text-zinc-500">
              {getTotalDuration(config.subjects)}
            </TableCell>
            <TableCell className="text-zinc-500">
              {getTotalQuestions(config.subjects)}
            </TableCell>
            <TableCell>
              <span className="text-zinc-500">
                {getCalculatorSubjects(config.subjects)} of{" "}
                {config.subjects.length} subjects
              </span>
            </TableCell>
            <TableCell className="text-zinc-500">
              {new Date(config.created_at).toLocaleDateString()}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ExamConfigListTable;
